/**
 * adapter.js - Bubble Pop, as the trial loop sees it.
 *
 * The trial loop (`shared/trialLoop.js`) is game-agnostic. This is the set of
 * questions it has to ask, and everything in it is something the engine does
 * not know and cannot infer: which words, in what combinations, said how, and
 * judged against what.
 *
 * ONE adapter is used by the React component AND by the test harness, so the
 * session a test plays is the session a child plays. Anything a test can only
 * do by reimplementing part of a game is a part of the game nothing tests.
 *
 * The component decorates each target with what only it can know - which
 * bubbles are still on screen, and their live states - before handing it to the
 * loop. Nothing here reaches into React.
 */

import { GAME } from './content/index.js';
import { getLevelContent, pacingFor } from './content/levels.js';
import { canonicalPhrase } from './content/objects.js';
import { judgeAttempt } from './logic/judgeContent.js';
import { buildPrompt, buildHint } from './logic/prompts.js';
import { createRng, planLevel, recentFrom } from '../shared/rounds.js';
import { createLineBag } from '../shared/lineBag.js';
import { requiredCapsFor } from '../../../engine/contract.js';

/**
 * @param {object} [options]
 * @param {number} [options.seed]
 * @param {() => number} [options.rng] share one stream with the rest of a session
 * @returns {object} the game, in the shape the trial loop consumes
 */
export function createAdapter({ seed = 1, rng } = {}) {
  const random = rng || createRng(seed);
  const bags = {};

  /**
   * One anti-repeat bag per (phase, rung) pool, so the same rung does not say
   * the same sentence twice running. Section 8.5 is about what the CHILD hears,
   * not about praise alone.
   */
  const choose = (key) => (templates) => {
    if (!bags[key] || bags[key].size !== templates.length) {
      bags[key] = createLineBag(templates, { rng: random });
    }
    return bags[key].next();
  };

  // A level is replayed until its boxes have ten plays each, and a word memory
  // that resets at every pass boundary is a memory with a seam in it: the child
  // hears the same word twice across it.
  let memory = { key: null, previous: null, recent: [] };

  return {
    id: GAME.id,
    capabilities: GAME.capabilities,
    coverage: {
      phasesAvailable: GAME.phasesAvailable,
      levelsAvailable: GAME.levelsAvailable,
    },

    has: (phase, level) => Boolean(getLevelContent(phase, level)),
    content: (phase, level) => getLevelContent(phase, level),
    nominalRung: (phase, level) => getLevelContent(phase, level).nominalRung,
    pacing: (phase, level) => pacingFor(getLevelContent(phase, level)),
    requiredCaps: (phase, level) => requiredCapsFor(phase, level, GAME.capabilities),

    /** One pass through a level: rounds, each with its field and its targets. */
    plan(phase, level) {
      const content = getLevelContent(phase, level);
      const key = `${phase}.${level}`;
      const same = memory.key === key;
      const rounds = planLevel(content, random, {
        previous: same ? memory.previous : null,
        recent: same ? memory.recent : [],
      });
      memory = {
        key,
        previous: rounds[rounds.length - 1] || null,
        recent: recentFrom(rounds),
      };
      return rounds;
    },

    phraseFor: (target) => canonicalPhrase(target),
    onScreen: (target) => target.onScreen || [target.objectId],
    gridState: (target) => target.gridState || null,
    targetPosition: (target) => `slot-${target.slot ?? 0}`,
    // Section 8.2, phase 3: word frames and object choices are on screen at
    // every rung except the gestural one, where the point is that Pip only points.
    optionsVisible: (phase, rungId) => phase === 3 && rungId !== 'gestural',

    prompt: ({ phase, rungId, target, content }) => buildPrompt({
      phase, rungId, target, content, choose: choose(`p${phase}-${rungId}`),
    }),

    hint: ({ phase, target, content }) => buildHint({
      phase, target, content, choose: choose(`hint-${phase}`),
    }),

    judge: ({ phase, target, trial, transcript, alternatives }) => judgeAttempt({
      phase,
      target,
      trial,
      transcript,
      alternatives,
      // At phase 3 the child may legitimately ask for something other than the
      // one Pip suggested. The world answers the request that was made.
      choices: phase === 3
        ? { objects: target.onScreen || [target.objectId] }
        : null,
    }),
  };
}

export default { createAdapter };
